import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import './CreateClient.css';

const CreateClient = () => {
  const [clientData, setClientData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    phone2: '',
    company: '',
    title: '',
    website: '',
    address: '',
    clientNotes: '',
  });

  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [atClientLimit, setAtClientLimit] = useState(false);
  const navigate = useNavigate();

  // Check client limit on mount
  useEffect(() => {
    const checkLimit = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/billing/limits`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.plan === 'free') {
          const clientsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/clients`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (clientsResponse.data.length >= response.data.limits.maxClients) {
            setAtClientLimit(true);
          }
        }
      } catch (error) {
        console.error('Error checking client limit:', error);
      }
    };
    checkLimit();
  }, []);

  // Function to format phone numbers as (XXX) XXX-XXXX
  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, ''); // Remove all non-numeric characters
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);

    if (match) {
      if (match[2]) {
        return `(${match[1]}) ${match[2]}${match[3] ? `-${match[3]}` : ''}`;
      }
      if (match[1]) {
        return `(${match[1]}`;
      }
    }

    return value;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Apply formatting for phone input
    if (name === 'phone' || name === 'phone2') {
      setClientData({
        ...clientData,
        [name]: formatPhoneNumber(value),
      });
    } else {
      setClientData({
        ...clientData,
        [name]: value,
      });
    }
  };

  const validateForm = () => {
    const { firstName, lastName, email, phone } = clientData;
    
    if (!firstName.trim() || !lastName.trim()) {
      setErrorMessage('First name and last name are required.');
      return false;
    }
    
    if (!email.trim()) {
      setErrorMessage('Email address is required.');
      return false;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrorMessage('Please enter a valid email address.');
      return false;
    }
    
    if (!phone.trim()) {
      setErrorMessage('Phone number is required.');
      return false;
    }
    
    // Check if phone has enough digits
    const phoneDigits = phone.replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      setErrorMessage('Please enter a complete phone number.');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      const token = localStorage.getItem('token');

      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/clients`, clientData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 201) {
        console.log('Client created successfully');
        navigate('/clients'); // Redirect to clients list
      } else {
        console.error('Failed to create client');
        setErrorMessage('Failed to create client. Please try again.');
      }
    } catch (error) {
      console.error('Error creating client:', error);
      if (error.response?.status === 403 && error.response?.data?.upgradeRequired) {
        setAtClientLimit(true);
        setErrorMessage(error.response.data.message);
      } else if (error.response?.data?.message) {
        setErrorMessage(error.response.data.message);
      } else {
        setErrorMessage('An error occurred while creating the client. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Navigate back to the dashboard
  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  const handleBackToClients = () => {
    navigate('/clients');
  };

  return (
    <div className="cc-page">

      {/* Nav */}
      <button className="cc-back-btn" onClick={() => navigate('/clients')}>
        ← Clients
      </button>

      {/* Header */}
      <div className="cc-header">
        <h1 className="cc-title">Add new client</h1>
        <p className="cc-subtitle">Fill in the details below — only name, email and phone are required</p>
      </div>

      {/* Client limit banner */}
      {atClientLimit && (
        <div className="cc-limit-banner">
          <div>
            <p className="cc-limit-title">Client limit reached</p>
            <p className="cc-limit-msg">You've reached the maximum of 3 clients on the Free plan.</p>
          </div>
          <button className="cc-upgrade-btn" onClick={() => navigate('/pricing')}>
            Upgrade to Pro
          </button>
        </div>
      )}

      {/* Error */}
      {errorMessage && (
        <div className="cc-error">
          ⚠️ {errorMessage}
        </div>
      )}

      {/* Form */}
      <div className="cc-card">
        <form onSubmit={handleSubmit} className="cc-form">

          {/* Name row */}
          <div className="cc-row">
            <div className="cc-field">
              <label htmlFor="firstName">First name</label>
              <input type="text" id="firstName" name="firstName" value={clientData.firstName}
                onChange={handleChange} placeholder="First name" required disabled={isLoading} />
            </div>
            <div className="cc-field">
              <label htmlFor="lastName">Last name</label>
              <input type="text" id="lastName" name="lastName" value={clientData.lastName}
                onChange={handleChange} placeholder="Last name" required disabled={isLoading} />
            </div>
          </div>

          {/* Email */}
          <div className="cc-field">
            <label htmlFor="email">Email address</label>
            <input type="email" id="email" name="email" value={clientData.email}
              onChange={handleChange} placeholder="email@example.com" required disabled={isLoading} />
          </div>

          {/* Phone row */}
          <div className="cc-row">
            <div className="cc-field">
              <label htmlFor="phone">Phone number</label>
              <input type="tel" id="phone" name="phone" value={clientData.phone}
                onChange={handleChange} placeholder="(XXX) XXX-XXXX" required maxLength="14" disabled={isLoading} />
            </div>
            <div className="cc-field">
              <label htmlFor="phone2">Secondary phone <span className="cc-opt">optional</span></label>
              <input type="tel" id="phone2" name="phone2" value={clientData.phone2}
                onChange={handleChange} placeholder="(XXX) XXX-XXXX" maxLength="14" disabled={isLoading} />
            </div>
          </div>

          {/* Company + Title row */}
          <div className="cc-row">
            <div className="cc-field">
              <label htmlFor="company">Company <span className="cc-opt">optional</span></label>
              <input type="text" id="company" name="company" value={clientData.company}
                onChange={handleChange} placeholder="Company name" disabled={isLoading} />
            </div>
            <div className="cc-field">
              <label htmlFor="title">Title <span className="cc-opt">optional</span></label>
              <input type="text" id="title" name="title" value={clientData.title}
                onChange={handleChange} placeholder="Job title" disabled={isLoading} />
            </div>
          </div>

          {/* Website */}
          <div className="cc-field">
            <label htmlFor="website">Website <span className="cc-opt">optional</span></label>
            <input type="url" id="website" name="website" value={clientData.website}
              onChange={handleChange} placeholder="https://company.com" disabled={isLoading} />
          </div>

          {/* Address */}
          <div className="cc-field">
            <label htmlFor="address">Address <span className="cc-opt">optional</span></label>
            <input type="text" id="address" name="address" value={clientData.address}
              onChange={handleChange} placeholder="Street, City, State, ZIP" disabled={isLoading} />
          </div>

          {/* Notes */}
          <div className="cc-field">
            <label htmlFor="clientNotes">Notes <span className="cc-opt">optional</span></label>
            <textarea id="clientNotes" name="clientNotes" value={clientData.clientNotes}
              onChange={handleChange} placeholder="Any initial notes about this client..."
              rows="3" disabled={isLoading} />
          </div>

          {/* Submit */}
          <button type="submit" className={`cc-submit ${isLoading ? 'loading' : ''}`} disabled={isLoading || atClientLimit}>
            {isLoading ? (
              <span className="cc-loading"><span className="cc-spinner" /> Creating...</span>
            ) : (
              'Create client'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateClient;